import classnames from 'classnames';
import { useEffect, type FunctionComponent } from 'react';

import { TextSkeleton } from '@/core/tamagoshi/TextSkeleton/TextSkeleton';
import { Gtm } from '@/core/tracking/utils/Gtm';
import { useBooleanFlags } from '@/domains/core/flags/flags.hooks';
import { ManoExpressLargeLogo } from '@/domains/core/tamagoshi/Logos/ManoExpressLargeLogo/ManoExpressLargeLogo';
import { P } from '@/domains/core/tamagoshiTailwind/components/Typography/Typography';
import { PRODUCT_PAGE_REVAMP } from '@/domains/productDelivery/constants/FeatureFlags';
import { DeliveryWordings } from '@/domains/productDiscovery/ProductPage/common/modules/Delivery/DeliveryWordings';
import {
  useDeliveryState,
  type DeliveryOffer,
} from '@/productDiscovery/ProductPage/common/modules/Delivery/delivery.ducks';
import { DeliveryCountries } from '@/productDiscovery/ProductPage/common/modules/Delivery/DeliveryCountries/DeliveryCountries.default';
import {
  deliveryOptionsAvailable,
  deliveryOptionsAvailableAndReturns,
} from '@/productDiscovery/ProductPage/common/modules/Delivery/translations';
import { DeliveryBullets } from '@/productDiscovery/ProductPage/common/modules/GoodToKnow/GoodToKnowDelivery';
import { ReturnPolicyBullet } from '@/productDiscovery/ProductPage/common/modules/GoodToKnow/GoodToKnowReturns';
import { useDetail } from '@/productDiscovery/ProductPage/hooks/useDetail';

import { Bullet } from '../GoodToKnow/Bullet';
import { FulfillmentContent } from '../GoodToKnow/GoodToKnowUtils';

import styles from './deliveryPlace.module.scss';

export const DeliveryPlace: FunctionComponent = () => {
  const { offers, fetching, fetched } = useDeliveryState();
  const { delivery, isSellable } = useDetail();
  const deliveryFullfilled = delivery?.fulfillment;

  const [isProductPageRevampEnabled] = useBooleanFlags([PRODUCT_PAGE_REVAMP]);

  useEffect(() => {
    if (!offers?.length) return;

    Gtm.push({
      event: 'interaction_display delivery informations',
      event_trigger: 'product_page',
      amp_event_name: 'Display delivery informations',
      page_type: 'product',
      delivery_count: offers.length,
      delivery_options: offers,
    });
  }, [offers, offers?.length]);

  if (!isSellable) return null;
  if (fetched && !offers?.length) return null;
  if (!fetching && !fetched) return <div style={{ height: 208 }} />;

  if (fetching && !offers?.length) {
    return (
      <div>
        <div className="flex justify-between">
          {isProductPageRevampEnabled ? (
            <P className="mb-[14px]" weight="semibold" scale="body1">
              {deliveryOptionsAvailableAndReturns}
            </P>
          ) : (
            <P className="mb-[14px]" weight="bold" scale="body1">
              {deliveryOptionsAvailable}
            </P>
          )}
        </div>
        <ul className={styles.deliveryPlacesList} data-testid="delivery-list">
          <li>
            <TextSkeleton lines={3} />
          </li>
          <li>
            <TextSkeleton lines={3} />
          </li>
        </ul>
      </div>
    );
  }

  return (
    <>
      <div className="flex justify-between">
        {isProductPageRevampEnabled ? (
          <P className="mb-[14px]" weight="semibold" scale="body1">
            {deliveryOptionsAvailableAndReturns}
          </P>
        ) : (
          <P className="mb-[14px]" weight="bold" scale="body1">
            {deliveryOptionsAvailable}
          </P>
        )}
        {deliveryFullfilled && (
          <ul className={classnames(styles.list, styles.manoExpressLogo)}>
            <Bullet
              icon={
                <ManoExpressLargeLogo
                  disableOptimizer
                  data-testid="manomanoExpressLogo"
                />
              }
              modalContent={<FulfillmentContent />}
            />
          </ul>
        )}
      </div>
      <ul className={styles.deliveryPlacesList} data-testid="delivery-list">
        {offers.map((offer: DeliveryOffer) => {
          return (
            <div key={offer.name}>
              <DeliveryWordings offer={offer} />
            </div>
          );
        })}
      </ul>
      <ul className={styles.list}>
        {!isProductPageRevampEnabled && !deliveryFullfilled && (
          <DeliveryBullets />
        )}
        <ReturnPolicyBullet />
      </ul>
      {!isProductPageRevampEnabled && <DeliveryCountries />}
    </>
  );
};
